import React, { useState, useEffect } from "react";
import axios from "axios";
import "./PaymentPage.css";

const getApiBase = () => {
  const base =
    process.env.REACT_APP_API_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");
  return base.replace(/\/api\/?$/, "");
};
const getPaymentApiUrl = (path) => {
  const base = getApiBase();
  return `${base}/api${path.startsWith("/") ? path : "/" + path}`;
};
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "";

/** Razorpay India: contact must be full digits e.g. 919876543210 (no masking). Normalizes +91 / 10-digit / 12-digit. */
function normalizePrefillForRazorpay(raw) {
  const name = String(raw?.name || "Customer").trim() || "Customer";
  const emailRaw = String(raw?.email || "").trim();
  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRaw) ? emailRaw : "";
  const contact = String(raw?.contact || "").trim();
  const digits = contact.replace(/\D/g, "");
  let c = "";
  if (digits.length >= 12 && digits.startsWith("91")) {
    c = digits.slice(0, 12);
  } else if (digits.length === 10) {
    c = "91" + digits;
  } else if (digits.length === 11 && digits.startsWith("0")) {
    c = "91" + digits.slice(1);
  } else if (digits.length > 10) {
    c = digits.slice(-12);
    if (!c.startsWith("91") && digits.length >= 10) c = "91" + digits.slice(-10);
  }
  if (!c) c = digits;
  return { name, email, contact: c };
}

const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const SuccessScreen = ({ redirectUrl }) => (
  <div className="main-wrapper">
    <div className="payment-card">
      <div
        className="success-icon"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: "#dcfce7",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px",
        }}
      >
        <span style={{ fontSize: "40px" }}>✅</span>
      </div>
      <h1 style={{ fontSize: "24px", color: "#166534", marginBottom: "8px" }}>
        Payment Successful!
      </h1>
      <p style={{ color: "#64748b", marginTop: "8px", fontSize: "14px" }}>
        Challan payment updated in DigitalVaahan.
      </p>
      <button
        onClick={() => (window.location.href = redirectUrl)}
        className="btn-action bg-gradient-success success-btn"
        style={{ marginTop: "28px" }}
      >
        Go to Dashboard →
      </button>
    </div>
  </div>
);

const PaymentPage = () => {
  const [status, setStatus] = useState("loading"); // loading | valid | error | processing | success
  const [error, setError] = useState("");
  const [challan, setChallan] = useState(null);
  const [amount, setAmount] = useState(0);
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [razorpayOrder, setRazorpayOrder] = useState(null);
  const [isWalletRechargeFlow, setIsWalletRechargeFlow] = useState(false);
  const [returnUrlForRecharge, setReturnUrlForRecharge] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let token = params.get("token");

    if (!token && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.replace("#", ""));
      token = hashParams.get("token");
    }
    if (!token) {
      token = sessionStorage.getItem("payment_token");
      if (token) sessionStorage.removeItem("payment_token");
    }

    console.log("Payment page token:", token);

    if (!token) {
      setStatus("error");
      setError(
        "Missing token. Use the payment link from the main app."
      );
      return;
    }

    setSessionToken(token);
    const returnUrlParam = params.get("return_url");
    const typeParam = params.get("type");
    const isWalletFromUrl =
      typeParam === "wallet_recharge" ||
      (returnUrlParam && (
        returnUrlParam.includes("recharge_success=1") ||
        returnUrlParam.includes("/user/wallet") ||
        returnUrlParam.includes("amount_paise=")
      ));
    if (isWalletFromUrl) {
      setIsWalletRechargeFlow(true);
      setReturnUrlForRecharge(returnUrlParam || "");
    }
    verifySession(token);
  }, []);

  const copyChallanNo = () => {
    const no = registrationNumber || challan?.challan_no || challan?.challanNumber;
    if (no) {
      navigator.clipboard.writeText(no);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRetry = () => {
    setStatus("loading");
    setError("");
    const params = new URLSearchParams(window.location.search);
    const token = sessionToken || params.get("token");
    if (token) verifySession(token);
    else setError("No token found. Use the payment link from the main app.");
  };

  const verifySession = async (token) => {
    setLoading(true);
    const verifyUrl = `${getPaymentApiUrl("/payment/verify-session")}?token=${encodeURIComponent(token)}`;
    console.log("[Payment] API base:", getPaymentApiUrl(""), "verify-session URL:", verifyUrl);
    try {
      const response = await axios.get(verifyUrl);

      const data = response.data;
      console.log("[Payment FLOW] verify-session response:", {
        success: data?.success,
        status: data?.status,
        hasChallan: !!data?.challan,
        hasData: !!data?.data,
        prefillFromChallan: data?.challan?.prefill,
        prefillFromData: data?.data?.prefill,
        userEmail: data?.challan?.userEmail ?? data?.data?.userEmail,
        userContact: data?.challan?.userContact ?? data?.data?.userContact,
      });

      // Support flat response: { challanId, registration_number, total_amount, items }
      const flatAmount = data.total_amount ?? data.amount;
      const flatRegNo = data.registration_number ?? data.registrationNumber;
      const items = data.items || data.challans || [];
      const firstChallanNo = items[0] && (items[0].challan_no ?? items[0].Challan_No ?? items[0].challanNumber);
      const challanNosFromItems = items
        .map((i) => i.challan_no ?? i.Challan_No ?? i.challanNumber)
        .filter(Boolean);
      const challanDisplayFromItems = challanNosFromItems.length > 0 ? challanNosFromItems.join(", ") : "";

      const success = data.success === true || data.status === true;
      const raw = data.challan || data.data || data;
      const challanData = raw && (raw !== data || success) ? {
        ...raw,
        amount: parseFloat(
          raw.amount ?? raw.fine_amount ?? raw.fine ?? raw.total_amount ?? raw.total ?? raw.payable_amount ?? raw.totalAmount ?? flatAmount ?? 0
        ) || 0,
        challan_no:
          raw.challan_no ??
          raw.challanNumber ??
          raw.challanNo ??
          raw.challan_number ??
          raw.registration_number ??
          challanDisplayFromItems ??
          firstChallanNo ??
          "",
        challanNumber:
          raw.challanNumber ??
          raw.challan_no ??
          raw.challanNo ??
          raw.challan_number ??
          raw.registration_number ??
          challanDisplayFromItems ??
          firstChallanNo ??
          "",
        order_id: raw.order_id ?? raw.razorpay_order_id,
        razorpay_key: raw.razorpay_key ?? raw.key,
      } : null;

      const resolvedAmount = challanData?.amount ?? (parseFloat(flatAmount) || 0);
      const resolvedRegNo =
        challanData?.challan_no ||
        challanData?.challanNumber ||
        challanDisplayFromItems ||
        firstChallanNo ||
        flatRegNo ||
        (data.challanId != null ? `Session #${data.challanId}` : "") ||
        "";

      console.log("Extracted challan display:", { resolvedRegNo, flatRegNo, challanDisplayFromItems, firstChallanNo, challanId: data.challanId });

      if (success) {
        setAmount(resolvedAmount);
        setRegistrationNumber(resolvedRegNo);
        const isWalletFromChallan = (resolvedRegNo || "").toLowerCase().includes("wallet recharge");
        if (isWalletFromChallan) {
          setIsWalletRechargeFlow(true);
          const urlReturn = new URLSearchParams(window.location.search).get("return_url");
          if (urlReturn) setReturnUrlForRecharge(urlReturn);
        }
        const sessionId = data.sessionId ?? data.challanId ?? data.challan?.id;
        const userId = data.userId ?? data.user_id ?? data.challan?.userId ?? data.challan?.user_id;
        const orderId = data.challan?.order_id ?? data.data?.order_id ?? challanData?.order_id;
        const rzpKey = data.challan?.razorpay_key ?? data.data?.razorpay_key ?? challanData?.razorpay_key;
        if (challanData) {
          const challanToSet = {
            ...challanData,
            order_id: orderId ?? challanData.order_id,
            orderId: orderId ?? challanData.order_id,
            razorpay_key: rzpKey ?? challanData.razorpay_key,
            amount: resolvedAmount,
            challan_no: resolvedRegNo,
            challanNumber: resolvedRegNo,
            sessionId,
            userId,
          };
          console.log("[Payment FLOW] setChallan with prefill:", {
            hasPrefill: !!challanToSet.prefill,
            prefill: challanToSet.prefill,
            userEmail: challanToSet.userEmail,
            userContact: challanToSet.userContact ? "***" + String(challanToSet.userContact).slice(-4) : undefined,
          });
          setChallan(challanToSet);
        } else {
          setChallan({
            amount: resolvedAmount,
            challan_no: resolvedRegNo,
            challanNumber: resolvedRegNo,
            registration_number: flatRegNo,
            order_id: orderId,
            orderId,
            razorpay_key: rzpKey,
            sessionId,
            userId,
          });
        }
        setStatus("valid");
        setError("");

        const loaded = await loadRazorpay();
        if (!loaded) console.warn("Razorpay script failed to load");
      } else {
        setStatus("error");
        setError(data?.message || data?.error || "Invalid session");
      }
    } catch (err) {
      setStatus("error");
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      console.error("[Payment] verify-session failed:", msg, err.response?.data);
      setError(msg || "Session verification failed. Check backend logs.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!challan) {
      setError("Challan data not loaded.");
      return;
    }

    const loaded = await loadRazorpay();
    if (!loaded || !window.Razorpay) {
      setError("Razorpay not loaded. Please refresh the page.");
      return;
    }

    setStatus("processing");

    const sessionId = challan.sessionId ?? challan.id ?? challan.challanId;
    const amt = challan?.amount ?? amount ?? 0;

    // Session-based flow (from Vahan portal): always create a fresh Razorpay order so amount matches and QR loads.
    // Using verify-session's order_id can open a stale/wrong order and Razorpay may not show QR.
    let orderData = razorpayOrder;
    const useSessionOrder = sessionId != null && amt > 0;

    if (useSessionOrder) {
      // Always call create-razorpay-order for session flow; don't use challan.order_id
      orderData = null;
    } else if (!orderData?.orderId && challan?.order_id && (challan.razorpay_key || RAZORPAY_KEY)) {
      orderData = {
        key: challan.razorpay_key || RAZORPAY_KEY,
        amount: Math.round((challan?.amount ?? amount ?? 0) * 100) || 100,
        orderId: challan.order_id,
        currency: challan.currency || "INR",
      };
      setRazorpayOrder(orderData);
    }

    if (!orderData?.orderId && sessionId != null && amt > 0) {
        try {
          const token =
            sessionToken ||
            new URLSearchParams(window.location.search).get("token") ||
            (window.location.hash ? new URLSearchParams(window.location.hash.replace("#", "")).get("token") : null);
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const createOrderBody = {
            sessionId,
            amount: amt,
            registration_number: registrationNumber || challan?.challan_no || challan?.challanNumber,
            userId: challan.userId ?? challan.user_id,
            token,
          };
          console.log("[Payment] create-razorpay-order request:", createOrderBody);
          const orderResponse = await axios.post(
            getPaymentApiUrl("/payment/create-razorpay-order"),
            createOrderBody,
            { headers }
          );
          console.log("[Payment FLOW] create-razorpay-order response:", {
            status: orderResponse.data?.status,
            orderId: orderResponse.data?.orderId ?? orderResponse.data?.order_id,
            amount: orderResponse.data?.amount,
            amountRupees: orderResponse.data?.amount != null ? (orderResponse.data.amount / 100).toFixed(2) : null,
            key: orderResponse.data?.key ? `${String(orderResponse.data.key).slice(0, 12)}...` : null,
            hasPrefill: !!(orderResponse.data?.prefill && Object.keys(orderResponse.data.prefill).length),
            prefillKeys: orderResponse.data?.prefill ? Object.keys(orderResponse.data.prefill) : [],
          });
          const res = orderResponse.data;
          const ok = (res?.status || res?.success) && (res?.orderId || res?.order_id);
          if (ok) {
            orderData = {
              key: res.key || RAZORPAY_KEY,
              amount: res.amount,
              orderId: res.orderId || res.order_id,
              currency: res.currency || "INR",
              ...(res.prefill && Object.keys(res.prefill).length ? { prefill: res.prefill } : {}),
            };
            console.log("[Payment FLOW] orderData built for Razorpay:", {
              hasOrderId: !!orderData.orderId,
              hasPrefill: !!orderData.prefill,
              prefillKeys: orderData.prefill ? Object.keys(orderData.prefill) : [],
            });
            setRazorpayOrder(orderData);
          }
        } catch (err) {
          console.error("[Payment] create-razorpay-order failed:", {
            message: err?.message,
            response: err?.response?.data,
            status: err?.response?.status,
          });
          setError("Could not create payment order. Please try again.");
          setStatus("valid");
          return;
        }
    }
    if (!orderData?.orderId) {
      setError("Payment order not ready. Please try again.");
      setStatus("valid");
      return;
    }

    const rzpKey = String(orderData.key || RAZORPAY_KEY || challan?.razorpay_key || "").trim();
    const descRaw = registrationNumber || challan?.challan_no || challan?.challanNumber || "";
    const description = String(descRaw).replace(/[\u{1F300}-\u{1F9FF}]|[\u2600-\u26FF]|[\u2700-\u27BF]/gu, "").replace(/[^\w\s\-.,]/g, "").trim().slice(0, 255) || "Challan payment";
    if (!rzpKey) {
      setError("Payment gateway key missing. Please refresh or contact support.");
      setStatus("valid");
      return;
    }

    const amountPaise = Number(orderData.amount) || Math.round((challan?.amount ?? amount ?? 0) * 100) || 100;
    const orderIdStr = String(orderData.orderId);
    const currency = orderData.currency || challan?.currency || "INR";
    // Key, order_id and amount must all come from the same source (verify-session or create-razorpay-order) to avoid Razorpay SERVER_ERROR.
    // Razorpay docs show amount as string in paise; pass as string for compatibility.
    const pRaw = orderData?.prefill || challan?.prefill || {};
    const prefillMerged = normalizePrefillForRazorpay({
      name: pRaw.name || challan?.userName || challan?.prefill?.name,
      email: pRaw.email || challan?.userEmail || challan?.prefill?.email,
      contact: pRaw.contact || challan?.userContact || challan?.prefill?.contact,
    });
    // Enable all methods and set UPI flow preference for smoother QR/UPI discovery.
    const options = {
      key: rzpKey,
      amount: String(amountPaise),
      currency,
      name: "DigitalVaahan",
      order_id: orderIdStr,
      description: `Challan ${description}`,
      method: {
        upi: true,
        card: true,
        netbanking: true,
        wallet: true,
      },
      upi: {
        flow: "collect",
      },
      config: {
        display: {
          language: "en",
        },
      },
      retry: {
        enabled: true,
        max_count: 3,
      },
      handler: async function (response) {
        try {
          // Wallet recharge: redirect to return_url so main app's verify-recharge can credit the wallet.
          // Use state (set at page load) - more reliable than window.location at payment time.
          const isWallet = isWalletRechargeFlow || (registrationNumber || challan?.challan_no || challan?.challanNumber || "").toLowerCase().includes("wallet recharge");
          const returnUrl = returnUrlForRecharge || new URLSearchParams(window.location.search).get("return_url");

          if (isWallet && returnUrl) {
            try {
              const url = new URL(returnUrl, window.location.origin);
              if (response?.razorpay_payment_id) url.searchParams.set("payment_id", response.razorpay_payment_id);
              if (response?.razorpay_order_id) url.searchParams.set("order_id", response.razorpay_order_id);
              if (response?.razorpay_signature) url.searchParams.set("razorpay_signature", response.razorpay_signature);
              console.log("[Payment FLOW] wallet recharge redirect:", {
                returnUrl: url.toString(),
                hasPaymentId: !!response?.razorpay_payment_id,
                hasOrderId: !!response?.razorpay_order_id,
                params: Object.fromEntries(url.searchParams),
              });
              window.location.href = url.toString();
              return;
            } catch (e) {
              console.warn("[Payment] wallet recharge redirect failed:", e?.message || e);
            }
          }
          if (isWallet && !returnUrl) {
            setError("Wallet recharge succeeded but return URL is missing. Please go to the main app Wallet page.");
            setStatus("error");
            return;
          }

          const verifyRes = await axios.post(
            getPaymentApiUrl("/payment/verify-payment"),
            {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            }
          );

          if (verifyRes.data.status && verifyRes.data.data?.redirect_url) {
            setRedirectUrl(verifyRes.data.data.redirect_url);
            setStatus("success");
          } else {
            setError("Payment verification failed. Please contact support.");
            setStatus("error");
          }
        } catch (err) {
          setError(
            err.response?.data?.message || "Payment verification failed"
          );
          setStatus("error");
        }
      },
      prefill: (() => {
        const prefillObj = { name: prefillMerged.name };
        if (prefillMerged.email) prefillObj.email = prefillMerged.email;
        if (prefillMerged.contact) prefillObj.contact = prefillMerged.contact;
        console.log("[Payment FLOW] prefill sources:", {
          fromOrderData: !!orderData?.prefill,
          fromChallan: !!challan?.prefill,
          normalizedContactLen: prefillMerged.contact ? prefillMerged.contact.length : 0,
          normalizedContactTail: prefillMerged.contact ? "***" + prefillMerged.contact.slice(-4) : "",
          hasEmail: !!prefillMerged.email,
          willShowRazorpayContactModal: !(prefillMerged.email && prefillMerged.contact),
        });
        return prefillObj;
      })(),
      theme: { color: "#3399cc" },
    };

    console.log("[Payment] Razorpay options (key masked):", {
      key: rzpKey ? `${rzpKey.slice(0, 12)}...` : "missing",
      order_id: orderIdStr,
      amount: amountPaise,
      amountRupees: (amountPaise / 100).toFixed(2),
      currency,
      description: options.description,
      methodsEnabled: options.method,
      upiFlow: options.upi?.flow,
      hasConfigDisplay: !!options.config?.display,
      retryEnabled: options.retry?.enabled,
    });
    console.log("[Payment] Razorpay prefill passed to SDK:", {
      name: options.prefill?.name,
      email: options.prefill?.email ? `${String(options.prefill.email).slice(0, 3)}…` : "(empty)",
      contactDigits: options.prefill?.contact ? String(options.prefill.contact).replace(/\D/g, "").length : 0,
      contactTail: options.prefill?.contact ? "***" + String(options.prefill.contact).slice(-4) : "(empty)",
    });

    try {
      if (typeof window.Razorpay !== "function") {
        setError("Razorpay not loaded. Please refresh the page and allow scripts.");
        setStatus("valid");
        return;
      }
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        console.error("[Payment] Razorpay payment.failed – full response:", response);
        console.error("[Payment] Razorpay payment.failed – error detail:", {
          code: response?.error?.code,
          description: response?.error?.description,
          reason: response?.error?.reason,
          step: response?.error?.step,
          source: response?.error?.source,
          metadata: response?.error?.metadata,
        });
        setError("Payment failed. Please try again.");
        setStatus("valid");
      });
      rzp.on("payment.cancel", () => {
        console.log("[Payment] Razorpay payment.cancel");
        setStatus("valid");
      });
      rzp.open();
    } catch (err) {
      console.error("[Payment] Razorpay open error – full:", err);
      console.error("[Payment] Razorpay open error – message:", err?.message, "stack:", err?.stack);
      setError("Could not open payment gateway. " + (err?.message || "Please try again or allow popups."));
      setStatus("valid");
    }
  };

  if (status === "success") {
    return <SuccessScreen redirectUrl={redirectUrl} />;
  }

  if (status === "loading") {
    return (
      <div className="main-wrapper">
        <div className="payment-card">
          <div className="loading-skeleton">
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p style={{ color: "#64748b", fontSize: "14px" }}>Verifying session...</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="main-wrapper">
        <div className="payment-card error-card">
          <div
            style={{
              width: "60px",
              height: "60px",
              backgroundColor: "#fef2f2",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <span style={{ fontSize: "28px" }}>⚠️</span>
          </div>
          <h2 style={{ color: "#dc2626", marginBottom: "12px", fontSize: "20px" }}>
            Something went wrong
          </h2>
          <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "16px" }}>{error}</p>
          <button onClick={handleRetry} className="btn-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (status !== "valid" || !challan) return null;

  return (
    <div className="main-wrapper">
      <div className="payment-card">
        <div className="brand-tag bg-gradient-primary">DigitalVaahan</div>

        <h2 style={{ fontSize: "20px", color: "#1a202c", marginBottom: "5px" }}>
          Secure Checkout
        </h2>
        <p style={{ fontSize: "12px", color: "#a0aec0" }}>
          Transaction encrypted via SSL
        </p>

        <div className="amount-container bg-gradient-primary">
          <p
            style={{
              fontSize: "11px",
              textTransform: "uppercase",
              margin: "0 0 5px 0",
              opacity: 0.8,
            }}
          >
            Amount to Pay
          </p>
          <h1 style={{ margin: 0, fontSize: "40px", fontWeight: "900" }}>
            ₹{amount || challan?.amount || 0}
          </h1>
        </div>

        <div style={{ textAlign: "left", margin: "20px 0", fontSize: "14px", color: "#475569" }}>
          <div
            className={`challan-row ${copied ? "copied" : ""}`}
            onClick={copyChallanNo}
            title="Click to copy"
          >
            <span>Challan No</span>
            <span style={{ fontWeight: "600", fontFamily: "monospace", fontSize: "13px" }}>
              {registrationNumber || challan?.challan_no || challan?.challanNumber || "-"}
            </span>
          </div>
          <p className="copy-hint">{copied ? "✓ Copied!" : "Click to copy"}</p>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 12px",
              background: "#f8fafc",
              borderRadius: "10px",
            }}
          >
            <span>Merchant</span>
            <span style={{ fontWeight: "600" }}>DigitalVaahan Ltd.</span>
          </div>
        </div>

        <button
          onClick={handlePayment}
          disabled={status === "processing"}
          className="btn-action bg-gradient-primary"
        >
          {status === "processing" ? (
            <>
              <span className="loader"></span>Opening Razorpay...
            </>
          ) : loading ? (
            "Loading..."
          ) : (
            `Pay ₹${amount || challan?.amount || 0} Now`
          )}
        </button>

        <div
          style={{
            marginTop: "20px",
            fontSize: "10px",
            color: "#cbd5e0",
            letterSpacing: "1px",
          }}
        >
          PCI-DSS COMPLIANT • 256-BIT SECURE
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
